import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import {
  getAllEvents,
  getEventById,
  updateEvent,
  deleteEvent,
  createEvent,
} from "../../Utils/apiServices";
import Button from "../Button";
import Card from "../Card";
import TextField from "../TextField";
import { Typography } from "../Typography";
import toast from "../../Utils/toastManager";

const EventList = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  
  const AVAILABLE_ROLES = ['student', 'mentor', 'donor', 'admin', 'ecosystem_partner', 'corporate_sponsor', 'sponsor', 'investor', 'program_team', 'volunteer', 'ignitenext_labs'];
  const ROLES_WITHOUT_VOLUNTEER = AVAILABLE_ROLES.filter(role => role !== 'volunteer');

  const EVENT_TYPE_LABELS = {
    workshop: "Workshop",
    bootcamp: "Bootcamp",
    seminar: "Seminar",
    conference: "Conference",
    networking: "Networking",
    brand_promotion: "Brand Promotion",
    ignitenext_operations: "IgniteNext Operations",
    ignitenext_user_research: "IgniteNext User Research",
    ignitenext_outreach: "IgniteNext Outreach",
    strategic_vision: "Strategic Vision",
  };
  const formatEventTypeLabel = (value) => EVENT_TYPE_LABELS[value] || (value ? String(value).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "-");
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    short_description: "",
    message: "",
    event_type: "workshop",
    start_at: "",
    end_at: "",
    is_active: true,
    target_roles: [],
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await getAllEvents();
      if (response.success && response?.data?.data) {
        setEvents(
          Array.isArray(response.data?.data)
            ? response.data?.data
            : []
        );
      }
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRoleToggle = (role) => {
    setFormData((prev) => {
      const currentRoles = prev.target_roles || [];
      if (currentRoles.includes(role)) {
        return {
          ...prev,
          target_roles: currentRoles.filter((r) => r !== role),
        };
      } else {
        return {
          ...prev,
          target_roles: [...currentRoles, role],
        };
      }
    });
  };

  const extractRolesFromVisibilities = (visibilities) => {
    if (!visibilities || !Array.isArray(visibilities)) return [];
    const roles = new Set();
    visibilities.forEach((visibility) => {
      if (visibility.target_role && Array.isArray(visibility.target_role)) {
        visibility.target_role.forEach((role) => {
          if (AVAILABLE_ROLES.includes(role)) {
            roles.add(role);
          }
        });
      }
    });
    return Array.from(roles);
  };

  const handleEdit = async (event) => {
    try {
      // Fetch full event details if needed
      const response = await getEventById(event.id);
      if (response.success && response.data?.data) {
        const eventData = response.data.data;
        const targetRoles = extractRolesFromVisibilities(eventData.visibilities);
        
        setEditingId(event.id);
        setShowCreateForm(false);
        setFormData({
          title: eventData.title || "",
          description: eventData.description || "",
          short_description: eventData.short_description || "",
          message: eventData.message || "",
          event_type: eventData.event_type || "workshop",
          start_at: toLocalDateTime(eventData.start_at),
          end_at: toLocalDateTime(eventData.end_at),
          is_active: eventData.is_active !== undefined ? eventData.is_active : true,
          target_roles: targetRoles,
        });
      } else {
        // Fallback to the event data we already have
        const targetRoles = extractRolesFromVisibilities(event.visibilities);
        
        setEditingId(event.id);
        setShowCreateForm(false);
        setFormData({
          title: event.title || "",
          description: event.description || "",
          short_description: event.short_description || "",
          message: event.message || "",
          event_type: event.event_type || "workshop",
          start_at: toLocalDateTime(event.start_at),
          end_at: toLocalDateTime(event.end_at),
          is_active: event.is_active !== undefined ? event.is_active : true,
          target_roles: targetRoles,
        });
      }
    } catch (error) {
      console.error("Error fetching event details:", error);
      // Fallback to the event data we already have
      const targetRoles = extractRolesFromVisibilities(event.visibilities);
      
      setEditingId(event.id);
      setShowCreateForm(false);
      setFormData({
        title: event.title || "",
        description: event.description || "",
        short_description: event.short_description || "",
        message: event.message || "",
        event_type: event.event_type || "workshop",
        start_at: event.start_at
          ? new Date(event.start_at).toISOString().slice(0, 16)
          : "",
        end_at: event.end_at
          ? new Date(event.end_at).toISOString().slice(0, 16)
          : "",
        is_active: event.is_active !== undefined ? event.is_active : true,
        target_roles: targetRoles,
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setShowCreateForm(false);
    setFormData({
      title: "",
      description: "",
      short_description: "",
      message: "",
      event_type: "workshop",
      start_at: "",
      end_at: "",
      is_active: true,
      target_roles: [],
    });
  };

  const handleSave = async (eventId = null) => {
    try {
      // Convert date strings to ISO format for API
      const dataToSave = {
        title: formData.title,
        description: formData.description,
        short_description: formData.short_description || null,
        message: formData.message || null,
        event_type: formData.event_type,
        start_at: formData.start_at ? new Date(formData.start_at).toISOString() : null,
        end_at: formData.end_at ? new Date(formData.end_at).toISOString() : null,
        is_active: formData.is_active,
      };

      // Add target_role array based on selected roles
      // If roles are selected, send the array, otherwise send null to remove visibility
      dataToSave.target_role = formData.target_roles && formData.target_roles.length > 0 
        ? formData.target_roles 
        : null;

      let response;
      if (eventId) {
        // Update existing event
        response = await updateEvent(eventId, dataToSave);
      } else {
        // Create new event
        response = await createEvent(dataToSave);
      }

      if (response.success) {
        await fetchEvents();
        handleCancelEdit();
      } else {
        toast.error(
          `Failed to ${eventId ? "update" : "create"} event: ${
            response.error || "Unknown error"
          }`
        );
      }
    } catch (error) {
      console.error(`Error saving event:`, error);
      toast.error(`An error occurred while ${eventId ? "updating" : "creating"} the event`);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) {
      return;
    }

    try {
      const response = await deleteEvent(eventId);
      if (response.success) {
        await fetchEvents();
      } else {
        toast.error("Failed to delete event: " + (response.error || "Unknown error"));
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("An error occurred while deleting the event");
    }
  };

  // Convert UTC ISO string to local datetime-local format
  const toLocalDateTime = (isoString) => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Strip HTML tags from text for preview/search purposes
  const stripHtmlTags = (html) => {
    if (!html) return "";
    // Create a temporary div element to parse HTML
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  // Filter events based on search term
  const filteredEvents = useMemo(() => {
    if (!searchTerm.trim()) return events;
    
    const searchLower = searchTerm.toLowerCase();
    return events.filter((event) => {
      const title = (event.title || "").toLowerCase();
      // Strip HTML tags from description for searching
      const description = stripHtmlTags(event.description || "").toLowerCase();
      const shortDescription = (event.short_description || "").toLowerCase();
      const eventType = (event.event_type || "").toLowerCase();
      
      return (
        title.includes(searchLower) ||
        description.includes(searchLower) ||
        shortDescription.includes(searchLower) ||
        eventType.includes(searchLower)
      );
    });
  }, [events, searchTerm]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Typography as="p" className="text-tertiary-600">
          Loading events...
        </Typography>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <Typography as="h2" className="text-2xl font-semibold text-primary-900">
          Events
        </Typography>
        <Button
          onClick={() => {
            setShowCreateForm(true);
            setEditingId(null);
            setFormData({
              title: "",
              description: "",
              short_description: "",
              message: "",
              event_type: "workshop",
              start_at: "",
              end_at: "",
              is_active: true,
              target_roles: [],
            });
          }}
          startIcon={<Icon icon="lucide:plus" className="h-4 w-4" />}
          className="w-full sm:w-auto"
        >
          Create Event
        </Button>
      </div>

      {showCreateForm && (
        <Card className="border border-slate-200 p-6">
          <div className="space-y-4">
            <Typography
              as="h3"
              className="text-base font-semibold text-primary-900 mb-4"
            >
              Create New Event
            </Typography>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextField
                name="title"
                label="Title"
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                required
                placeholder="Enter event title"
              />
              <div>
                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                  Event Type
                </label>
                <select
                  name="event_type"
                  value={formData.event_type}
                  onChange={(e) => handleInputChange("event_type", e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="workshop">Workshop</option>
                  <option value="bootcamp">Bootcamp</option>
                  <option value="seminar">Seminar</option>
                  <option value="conference">Conference</option>
                  <option value="networking">Networking</option>
                  <option value="brand_promotion">Brand Promotion</option>
                  <option value="ignitenext_operations">IgniteNext Operations</option>
                  <option value="ignitenext_user_research">IgniteNext User Research</option>
                  <option value="ignitenext_outreach">IgniteNext Outreach</option>
                  <option value="strategic_vision">Strategic Vision</option>
                </select>
              </div>
              <TextField
                name="start_at"
                label="Start Date & Time"
                type="datetime-local"
                value={formData.start_at}
                onChange={(e) => handleInputChange("start_at", e.target.value)}
                required
              />
              <TextField
                name="end_at"
                label="End Date & Time"
                type="datetime-local"
                value={formData.end_at}
                onChange={(e) => handleInputChange("end_at", e.target.value)}
              />
              <div>
                <label className="flex items-center gap-2">
                  <span className="text-sm font-medium text-primary-900">Active:</span>
                  <button
                    type="button"
                    onClick={() => handleInputChange("is_active", !formData.is_active)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      formData.is_active
                        ? 'bg-green-600'
                        : 'bg-gray-400'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                        formData.is_active
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              </div>
              <div>
                <label className="flex items-center gap-2">
                  <span className="text-sm font-medium text-primary-900">Volunteer:</span>
                  <button
                    type="button"
                    onClick={() => handleRoleToggle('volunteer')}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                      formData.target_roles?.includes('volunteer')
                        ? 'bg-green-600'
                        : 'bg-gray-400'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                        formData.target_roles?.includes('volunteer')
                          ? 'translate-x-6'
                          : 'translate-x-1'
                      }`}
                    />
                  </button>
                </label>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                Target Roles
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 border border-slate-300 rounded-lg">
                {ROLES_WITHOUT_VOLUNTEER.map((role) => (
                  <label key={role} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.target_roles?.includes(role) || false}
                      onChange={() => handleRoleToggle(role)}
                      className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-primary-900 capitalize">
                      {role.replace(/_/g, ' ')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                Short Description
                <span className="ml-2 text-xs text-tertiary-600">
                  ({formData.short_description?.length || 0}/255)
                </span>
              </label>
              <textarea
                name="short_description"
                value={formData.short_description}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 255) {
                    handleInputChange("short_description", value);
                  }
                }}
                placeholder="Enter short description"
                rows={3}
                maxLength={255}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                Description
                <span className="ml-2 text-xs text-tertiary-600">
                  ({formData.description?.length || 0}/1024)
                </span>
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 1024) {
                    handleInputChange("description", value);
                  }
                }}
                placeholder="Enter event description"
                rows={4}
                maxLength={1024}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            {formData.target_roles?.includes('volunteer') && (
              <div>
                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                  Message
                  <span className="ml-2 text-xs text-tertiary-600">
                    ({formData.message?.length || 0}/255)
                  </span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 255) {
                      handleInputChange("message", value);
                    }
                  }}
                  placeholder="Enter message for volunteers"
                  rows={3}
                  maxLength={255}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              <Button variant="secondary" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={() => handleSave()}>Create Event</Button>
            </div>
          </div>
        </Card>
      )}

      <Card 
        className="border border-slate-200 overflow-hidden w-full"
        header={
          <div className="flex items-center justify-end w-full">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="w-48 px-3 py-1.5 pl-8 rounded-lg border border-slate-200 bg-white text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue"
              />
              <Icon
                icon="lucide:search"
                className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-slate-400"
              />
            </div>
          </div>
        }
      >
        {events.length === 0 ? (
          <div className="p-6 text-center">
            <Typography as="p" className="text-tertiary-600">
              No events found.
            </Typography>
          </div>
        ) : (
          <div className="overflow-x-auto w-full">
              <table className="w-full min-w-[800px]">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Event Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Start Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    End Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-primary-900 uppercase tracking-wider align-middle">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {filteredEvents.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-8 text-center">
                      <Typography as="p" className="text-tertiary-600">
                        No events found matching your search.
                      </Typography>
                    </td>
                  </tr>
                ) : (
                  filteredEvents.map((event) => (
                  <React.Fragment key={event.id}>
                    {editingId === event.id ? (
                      <tr className="bg-slate-50">
                        <td colSpan="7" className="px-6 py-4">
                          <div className="space-y-4">
                            <Typography
                              as="h3"
                              className="text-base font-semibold text-primary-900 mb-4"
                            >
                              Edit Event
                            </Typography>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <TextField
                                name="title"
                                label="Title"
                                type="text"
                                value={formData.title}
                                onChange={(e) =>
                                  handleInputChange("title", e.target.value)
                                }
                                required
                                placeholder="Enter event title"
                              />
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  Event Type
                                </label>
                                <select
                                  name="event_type"
                                  value={formData.event_type}
                                  onChange={(e) =>
                                    handleInputChange("event_type", e.target.value)
                                  }
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                >
                                  <option value="workshop">Workshop</option>
                                  <option value="bootcamp">Bootcamp</option>
                                  <option value="seminar">Seminar</option>
                                  <option value="conference">Conference</option>
                                  <option value="networking">Networking</option>
                                  <option value="brand_promotion">Brand Promotion</option>
                                  <option value="ignitenext_operations">IgniteNext Operations</option>
                                  <option value="ignitenext_user_research">IgniteNext User Research</option>
                                  <option value="ignitenext_outreach">IgniteNext Outreach</option>
                                  <option value="strategic_vision">Strategic Vision</option>
                                </select>
                              </div>
                              <TextField
                                name="start_at"
                                label="Start Date & Time"
                                type="datetime-local"
                                value={formData.start_at}
                                onChange={(e) =>
                                  handleInputChange("start_at", e.target.value)
                                }
                                required
                              />
                              <TextField
                                name="end_at"
                                label="End Date & Time"
                                type="datetime-local"
                                value={formData.end_at}
                                onChange={(e) =>
                                  handleInputChange("end_at", e.target.value)
                                }
                              />
                              <div>
                                <label className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-primary-900">Active:</span>
                                  <button
                                    type="button"
                                    onClick={() => handleInputChange("is_active", !formData.is_active)}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                      formData.is_active
                                        ? 'bg-green-600'
                                        : 'bg-gray-400'
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                                        formData.is_active
                                          ? 'translate-x-6'
                                          : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                </label>
                              </div>
                              <div>
                                <label className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-primary-900">Volunteer:</span>
                                  <button
                                    type="button"
                                    onClick={() => handleRoleToggle('volunteer')}
                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                                      formData.target_roles?.includes('volunteer')
                                        ? 'bg-green-600'
                                        : 'bg-gray-400'
                                    }`}
                                  >
                                    <span
                                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${
                                        formData.target_roles?.includes('volunteer')
                                          ? 'translate-x-6'
                                          : 'translate-x-1'
                                      }`}
                                    />
                                  </button>
                                </label>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                Target Roles
                              </label>
                              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 border border-slate-300 rounded-lg">
                                {ROLES_WITHOUT_VOLUNTEER.map((role) => (
                                  <label key={role} className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={formData.target_roles?.includes(role) || false}
                                      onChange={() => handleRoleToggle(role)}
                                      className="w-4 h-4 text-primary-600 border-slate-300 rounded focus:ring-primary-500"
                                    />
                                    <span className="text-sm font-medium text-primary-900 capitalize">
                                      {role.replace(/_/g, ' ')}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                Short Description
                                <span className="ml-2 text-xs text-tertiary-600">
                                  ({formData.short_description?.length || 0}/255)
                                </span>
                              </label>
                              <textarea
                                name="short_description"
                                value={formData.short_description}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 255) {
                                    handleInputChange("short_description", value);
                                  }
                                }}
                                placeholder="Enter short description"
                                rows={3}
                                maxLength={255}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                Description
                                <span className="ml-2 text-xs text-tertiary-600">
                                  ({formData.description?.length || 0}/1024)
                                </span>
                              </label>
                              <textarea
                                name="description"
                                value={formData.description}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length <= 1024) {
                                    handleInputChange("description", value);
                                  }
                                }}
                                placeholder="Enter event description"
                                rows={4}
                                maxLength={1024}
                                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              />
                            </div>
                            {formData.target_roles?.includes('volunteer') && (
                              <div>
                                <label className="block text-sm font-medium text-primary-900 mb-2 text-left">
                                  Message
                                  <span className="ml-2 text-xs text-tertiary-600">
                                    ({formData.message?.length || 0}/255)
                                  </span>
                                </label>
                                <textarea
                                  name="message"
                                  value={formData.message}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.length <= 255) {
                                      handleInputChange("message", value);
                                    }
                                  }}
                                  placeholder="Enter message for volunteers"
                                  rows={3}
                                  maxLength={255}
                                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                            )}
                            <div className="flex items-center justify-end gap-3">
                              <Button variant="secondary" onClick={handleCancelEdit}>
                                Cancel
                              </Button>
                              <Button onClick={() => handleSave(event.id)}>
                                Save
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 align-middle">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Icon
                                icon="lucide:calendar"
                                className="h-5 w-5 text-slate-500"
                              />
                            </div>
                            <button
                              onClick={() => navigate(`/events/${event.id}`)}
                              className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline cursor-pointer line-clamp-1 text-left"
                              title={event.title}
                            >
                              {event.title || "-"}
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 align-middle">
                          <Typography
                            as="span"
                            className="text-sm text-tertiary-600 line-clamp-2"
                            title={stripHtmlTags(event.description)}
                          >
                            {event.description
                              ? (() => {
                                  const plainText = stripHtmlTags(event.description);
                                  return plainText.length > 100 
                                    ? plainText.substring(0, 100) + "..."
                                    : plainText;
                                })()
                              : "-"}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 align-middle">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {formatEventTypeLabel(event.event_type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap align-middle">
                          <Typography
                            as="span"
                            className="text-sm text-primary-900"
                          >
                            {formatDate(event.start_at)}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap align-middle">
                          <Typography
                            as="span"
                            className="text-sm text-primary-900"
                          >
                            {formatDate(event.end_at)}
                          </Typography>
                        </td>
                        <td className="px-6 py-4 align-middle">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              event.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {event.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-left align-middle">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleEdit(event)}
                              startIcon={<Icon icon="lucide:edit" className="h-4 w-4" />}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="secondary"
                              size="small"
                              onClick={() => handleDelete(event.id)}
                              startIcon={
                                <Icon icon="lucide:trash-2" className="h-4 w-4" />
                              }
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default EventList;
